/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type ForwardRefExoticComponent, type ReactNode, type RefAttributes } from 'react';

import { css, jsx } from '@compiled/react';

import {
	cssMap,
	type StrictXCSSProp,
	type XCSSAllProperties,
	type XCSSAllPseudos,
} from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { type BoxProps } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	text: {
		textAlign: 'left',
	},

	icon: {
		textAlign: 'center',
	},

	number: {
		textAlign: 'right',
	},
});

export type BaseCellProps = {
	/**
	 * A percentage of pixel width of the table to apply to a column.
	 */
	width?: number;
	/**
	 * Horizontal alignment of content.
	 */
	align?: alignMapOptions;
	/**
	 * Whether the cell should render as a `td` or `th` element.
	 */
	as?: 'td' | 'th';
	/**
	 * Same behavior as the HTML attribute.
	 *
	 * @see 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/th#attr-scope'
	 */
	scope?: 'col' | 'row' | 'colgroup' | 'rowgroup';
	/**
	 * A `testId` prop is a unique string that appears as a data attribute `data-testid`
	 * in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
	/**
	 * Content of the cell.
	 */
	children?: ReactNode;
	/**
	 * Number of columns to span.
	 */
	colSpan?: number;
	/**
	 * controlled styles to apply to the cell.
	 */
	xcss?: StrictXCSSProp<XCSSAllProperties, XCSSAllPseudos>;
	/**
	 * Whether the cell is overriding the responsive column sizing.
	 */
	isOverridingResponsiveColumnSizing?: boolean;
} & Pick<
	BoxProps<'div'>,
	| 'paddingBlock'
	| 'paddingInline'
	| 'backgroundColor'
	| 'onMouseEnter'
	| 'onMouseLeave'
	| 'style'
	| 'onKeyDown'
	| 'tabIndex'
	| 'onMouseMove'
>;

/**
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-sort
 */
export type SortDirection = 'ascending' | 'descending' | 'none' | 'other';

type InternalBaseCellProps = BaseCellProps & {
	sortDirection?: SortDirection;
};

type alignMapOptions = 'text' | 'number' | 'icon';

const baseStyles = css({
	boxSizing: 'border-box',
	appearance: 'none',
	border: 'none',
	paddingBlock: token('space.100'),
	paddingInlineEnd: token('space.100'),
	'&:focus-visible': {
		boxShadow: `0 0 0 2px ${token('color.border.focused')} inset`,
		outline: 'none',
	},
	'&:focus': {
		boxShadow: `0 0 0 2px ${token('color.border.focused')} inset`,
	},
});

const removeFirstChildTdReset = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:first-of-type:not(style)': {
		paddingInlineStart: token('space.100'),
		borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:last-of-type:not(style)': {
		// Setting a CSS custom property lets RowCell's inline var() chain read this value
		// without needing any JS-level "isLastColumn" knowledge.
		'--charlie-col-border-width': token('border.width'),
		borderInlineEnd: `${token('border.width')} solid ${token('color.border')}`,
	},
});

// When platform-dst-shape-theme-default: outer border is drawn by table wrapper; first/last cells get no outer border.
const removeFirstChildTdResetShapeTheme = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:first-of-type:not(style)': {
		paddingInlineStart: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:last-of-type:not(style)': {
		'--charlie-col-border-width': '0px',
	},
});

const removeFirstChildThReset = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:first-of-type:not(style)': {
		borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
		paddingInlineStart: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:last-of-type:not(style)': {
		paddingInlineEnd: token('space.100'),
		borderInlineEnd: `${token('border.width')} solid ${token('color.border')}`,
	},
});

const removeFirstChildThResetShapeTheme = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:first-of-type:not(style)': {
		paddingInlineStart: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:last-of-type:not(style)': {
		paddingInlineEnd: token('space.100'),
	},
});

const lastColumnGrow = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:last-of-type:not(style)': {
		flexGrow: 1,
	},
});

/**
 * __BaseCell__
 *
 * @internal
 *
 * Basic cell element.
 */
export const BaseCell: ForwardRefExoticComponent<{
    /**
     * A percentage of pixel width of the table to apply to a column.
     */
    width?: number;
    /**
     * Horizontal alignment of content.
     */
    align?: alignMapOptions;
    /**
     * Whether the cell should render as a `td` or `th` element.
     */
    as?: "td" | "th";
    /**
     * Same behavior as the HTML attribute.
     *
     * @see 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/th#attr-scope'
     */
    scope?: "col" | "row" | "colgroup" | "rowgroup";
    /**
     * A `testId` prop is a unique string that appears as a data attribute `data-testid`
     * in the rendered code, serving as a hook for automated tests.
     */
    testId?: string;
    /**
     * Content of the cell.
     */
    children?: ReactNode;
    /**
     * Number of columns to span.
     */
    colSpan?: number;
    /**
     * controlled styles to apply to the cell.
     */
    xcss?: StrictXCSSProp<XCSSAllProperties, XCSSAllPseudos>;
    /**
     * Whether the cell is overriding the responsive column sizing.
     */
    isOverridingResponsiveColumnSizing?: boolean;
} & Pick<BoxProps<"div">, "backgroundColor" | "paddingBlock" | "paddingInline" | "style" | "tabIndex" | "onKeyDown" | "onMouseEnter" | "onMouseLeave" | "onMouseMove"> & {
    sortDirection?: SortDirection;
} & RefAttributes<HTMLTableCellElement>> = forwardRef<HTMLTableCellElement, InternalBaseCellProps>(
	(
		{
			testId,
			as: Component = 'td',
			xcss,
			children,
			scope,
			width,
			sortDirection,
			colSpan,
			onMouseEnter,
			onMouseLeave,
			align,
			style,
			onKeyDown,
			tabIndex,
			onMouseMove,
			isOverridingResponsiveColumnSizing,
		},
		ref,
	) => {
		const shapeThemeRefresh = fg('charlie-table-shape-refresh');
		const isTd = Component === 'td';
		const isTh = Component === 'th';
		const tdWithShapeTheme = isTd && shapeThemeRefresh;
		const tdDefault = isTd && !shapeThemeRefresh;
		const thWithShapeTheme = isTh && shapeThemeRefresh;
		const thDefault = isTh && !shapeThemeRefresh;

		return (
			// moving off of Box to support removing some css resets
			<Component
				ref={ref}
				scope={scope}
				tabIndex={tabIndex}
				onKeyDown={onKeyDown}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				style={{ width: width ? `${width}px` : undefined, ...style }}
				className={xcss}
				data-testid={testId}
				aria-sort={sortDirection}
				css={[
					baseStyles,
					tdWithShapeTheme && removeFirstChildTdResetShapeTheme,
					thWithShapeTheme && removeFirstChildThResetShapeTheme,
					tdDefault && removeFirstChildTdReset,
					thDefault && removeFirstChildThReset,
					align && styles[align],
					(isTd || isTh) && !isOverridingResponsiveColumnSizing && lastColumnGrow,
				]}
				colSpan={colSpan}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onMouseMove={onMouseMove}
			>
				{children}
			</Component>
		);
	},
);
